import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { Header } from "../../components/base/Header";
import {
  AppsSidebar,
  useFilteredCategories,
  type AppItem,
} from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { useTheme } from "../../context/ThemeContext";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { usePapeleraCounts } from "./usePapeleraCounts";
import { usePapeleraPermissions } from "./usePapeleraPermissions";

interface PapeleraLayoutProps {
  mode?: "fleet" | "internal";
}

export default function PapeleraLayout({ mode = "fleet" }: PapeleraLayoutProps) {
  const appId = mode === "internal" ? "papelera-interno" : "papelera";
  const basePath = mode === "internal" ? "/papelera-interno" : "/papelera";

  const user = useUserStore((state) => state.user);
  const filteredCategories = useFilteredCategories();
  const hasRedirected = useRef(false);

  const { isExpired, isAuthenticated, isInitialized } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      appId,
      t(`apps.${appId}.name`),
      t(`apps.${appId}.description`),
      faTrash,
      "bg-red-500",
    );
  }, [setActiveApp, t, appId]);

  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const companyUser = useCompaniesStore((state) => state.companyUser);
  const setCompanyUser = useCompaniesStore((state) => state.setCompanyUser);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const fetchCompany = useCompaniesStore((state) => state.fetchCompany);
  const [ownCompanyName, setOwnCompanyName] = useState<string | undefined>();

  const isReseller =
    (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  // Sincronizar companyId/companyUser desde localStorage al montar.
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    }
    if (storedCompanyUser && storedCompanyUser !== companyUser) {
      setCompanyUser(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReseller) {
      fetchAllCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReseller]);

  // Resolver el nombre de la compañía seleccionada. El reseller lo toma de
  // allCompanies; el admin (que no carga el listado) lo pide directo por id.
  useEffect(() => {
    if (!companyId) {
      setOwnCompanyName(undefined);
      return;
    }
    const found = allCompanies.find((c) => c.id === companyId);
    if (found) {
      setOwnCompanyName(found.name || found.business_name || undefined);
      return;
    }
    let active = true;
    fetchCompany(companyId)
      .then((res) => {
        // getCompany responde { message, data: company }; el company puede venir
        // envuelto, así que tomamos .data si existe.
        const company = ((res as { data?: unknown })?.data ?? res) as {
          name?: string;
          business_name?: string;
        };
        if (active) {
          setOwnCompanyName(company?.name || company?.business_name || undefined);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [companyId, allCompanies, fetchCompany]);

  // Redirección por sesión inválida/expirada.
  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  // El modo interno (Sistemas Internos) es solo para reseller.
  useEffect(() => {
    if (mode === "internal" && !isReseller && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate("/");
    }
  }, [mode, isReseller, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === appId) {
      closeAppsSidebar();
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  const { data: counts, isLoading: countsLoading } =
    usePapeleraCounts(companyId);
  const { manageableEntities } = usePapeleraPermissions();

  const menuItems = manageableEntities.map((entity) => ({
    id: entity.id,
    label: t(entity.labelKey, { defaultValue: entity.id }),
    icon: entity.icon,
    path: `${basePath}/${entity.id}`,
    count: counts?.[entity.id] ?? 0,
    loading: countsLoading,
  }));

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-[#1A1A1A]" : "bg-gray-50"}`}>
      <Header
        onOpenAppsSidebar={openAppsSidebar}
        onToggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems}
          isDarkMode={isDarkMode}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname.endsWith(itemId)}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id)}
                isDarkMode={isDarkMode}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          companyIndicator={{
            companyId,
            companyName: ownCompanyName,
            isReseller,
            isDarkMode,
          }}
          footerStats={{
            label: t("papelera.summary"),
            items: [
              {
                label: t("papelera.totalDeleted"),
                value: menuItems.reduce((acc, item) => acc + (item.count || 0), 0),
              },
            ],
          }}
        />
        <div className="flex-1 max-h-full overflow-hidden min-w-0 w-full">
          <Outlet />
        </div>
      </div>
      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={appId}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
